import { useState, useEffect } from 'react';
import { locationsService } from '@/pages/locations/services/locations.service';
import type { LocationShifts } from '@/pages/locations/types';

// Mock function hasta que el endpoint funcione
function getMockLocationShifts(locationId: string | number): LocationShifts {
  const today = new Date().toISOString().split('T')[0];

  // Convertir locationId a número
  let numericId: number;
  if (typeof locationId === 'string') {
    if (locationId.startsWith('loc-')) {
      numericId = parseInt(locationId.replace('loc-', '')) || 1;
    } else {
      numericId = parseInt(locationId) || 1;
    }
  } else {
    numericId = locationId || 1;
  }

  // Simular diferentes configuraciones de turnos según la ubicación
  if (numericId === 1) {
    // Macartur Centro - 2 turnos
    return {
      location_id: numericId,
      date: today,
      is_open: true,
      shifts: [
        {
          type: 'morning',
          name: 'Mañana',
          start_time: '07:00',
          end_time: '15:00'
        },
        {
          type: 'afternoon',
          name: 'Tarde',
          start_time: '15:00',
          end_time: '22:00'
        }
      ],
      shift_type: 'split'
    };
  } else {
    // Otras ubicaciones - turno único
    return {
      location_id: numericId,
      date: today,
      is_open: true,
      shifts: [
        {
          type: 'morning',
          name: 'Mañana',
          start_time: '08:00',
          end_time: '22:00'
        }
      ],
      shift_type: 'single'
    };
  }
}

export const useLocationShifts = (locationId?: string) => {
  const [shifts, setShifts] = useState<LocationShifts | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchShifts = async (id: string, date?: string) => {
    setLoading(true);
    setError(null);

    try {
      console.log('🔍 DEBUG useLocationShifts fetchShifts:', { id, date });

      // Temporalmente usar solo mock data hasta que el endpoint esté funcionando
      const mockShiftsData = getMockLocationShifts(id);
      console.log('📊 Using mock shifts data in useLocationShifts:', mockShiftsData);

      setShifts(mockShiftsData);
    } catch (err) {
      console.error('❌ Error in useLocationShifts fetchShifts:', err);
      setError(err instanceof Error ? err.message : 'Error al obtener turnos');

      // Fallback to mock data even on error
      const mockShiftsData = getMockLocationShifts(id);
      setShifts(mockShiftsData);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (locationId) {
      fetchShifts(locationId);
    }
  }, [locationId]);

  const refetch = () => {
    if (locationId) {
      fetchShifts(locationId);
    }
  };

  const getShiftDisplay = () => {
    // Si no hay data de shifts aún, usar mock data
    let shiftsData = shifts;
    if (!shiftsData && locationId) {
      shiftsData = getMockLocationShifts(locationId);
    }

    if (!shiftsData || !shiftsData.is_open) {
      return {
        text: 'Cerrado',
        type: 'none' as const
      };
    }

    if (shiftsData.shift_type === 'split') {
      return {
        text: 'Mañana/Tarde',
        type: 'split' as const,
        shifts: shiftsData.shifts
      };
    } else if (shiftsData.shift_type === 'single') {
      return {
        text: 'Mañana',
        type: 'single' as const,
        shifts: shiftsData.shifts
      };
    }

    return {
      text: 'Cerrado',
      type: 'none' as const
    };
  };

  return {
    shifts,
    loading,
    error,
    refetch,
    getShiftDisplay,
    fetchShifts
  };
};